import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Check as CheckIcon,
  Person as PersonIcon,
  Build as ServicesIcon,
  RateReview as ReviewIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import TeamMemberService from '../../services/teamMemberService';
import { getFunctionalItem, setFunctionalItem } from '../../utils/functionalStorage';
import { useAppData } from '../../context/AppDataContext';
import { DEFAULT_LANGUAGE, normalizeLanguage } from '../../constants/Languages';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import TeamMemberPersonalInfo from '../../components/team-member-registration/TeamMemberPersonalInfo';
import TeamMemberRolePermissions from '../../components/team-member-registration/TeamMemberRolePermissions';
import TeamMemberServices from '../../components/team-member-registration/TeamMemberServices';
import TeamMemberReview from '../../components/team-member-registration/TeamMemberReview';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import WizardStepNavigation from '../../components/common/WizardStepNavigation';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const TeamMemberUpdatePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { storeId, teamMemberId } = useParams();
  const { currentStore } = useAppData();

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneCountryCode: '+351',
    phoneNumber: '',
    jobTitle: '',
    genre: '',
    birthDate: null,
    language: DEFAULT_LANGUAGE,
    roleId: null,
    additionalPermissions: [],
    services: [],
    acceptsCustomerBookings: true
  });

  const [services, setServices] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const isCompletingUpdate = useRef(false);
  const servicesLoadingRef = useRef(false);
  const initialFormDataRef = useRef(null);
  const [savedAcceptsCustomerBookings, setSavedAcceptsCustomerBookings] = useState(true);

  // Storage keys for caching
  const CACHE_KEY = `bf_team_member_update_cache_${storeId}_${teamMemberId}`;
  const INITIAL_DATA_KEY = `bf_team_member_update_initial_${storeId}_${teamMemberId}`;

  // Load team member data on component mount
  useEffect(() => {
    const loadTeamMember = async () => {
      if (!storeId) {
        setSnackbar({
          open: true,
          message: t('teamMembers.error.noStoreSelected'),
          severity: 'error'
        });
        setLoading(false);
        return;
      }
      const requestKey = `getTeamMember-${teamMemberId}-${storeId}`;
      
      return requestDeduplicator.deduplicate(requestKey, async () => {
        try {
          setLoading(true);
          const teamMemberData = await TeamMemberService.getTeamMember(teamMemberId, storeId);

          let birthDate = null;
          if (teamMemberData.birthDate) {
            birthDate = new Date(teamMemberData.birthDate);
          }

          const transformedServices = (teamMemberData.services || []).map(service => ({
            serviceId: service.serviceId,
            customPrice: service.customPrice || null
          }));

          const initialFormData = {
            name: teamMemberData.name || '',
            email: teamMemberData.email || '',
            phoneCountryCode: teamMemberData.phone?.countryCode || '+351',
            phoneNumber: teamMemberData.phone?.number || '',
            jobTitle: teamMemberData.store?.jobTitle || '',
            genre: teamMemberData.genre?.toLowerCase() || '',
            birthDate,
            language: normalizeLanguage(teamMemberData.language) || DEFAULT_LANGUAGE,
            roleId: teamMemberData.store?.roleId || null,
            additionalPermissions: teamMemberData.store?.additionalPermissions || [],
            services: transformedServices,
            acceptsCustomerBookings: teamMemberData.store?.acceptsCustomerBookings !== false
          };

          initialFormDataRef.current = initialFormData;
          setSavedAcceptsCustomerBookings(initialFormData.acceptsCustomerBookings !== false);
          try {
            setFunctionalItem(INITIAL_DATA_KEY, JSON.stringify(initialFormData));
          } catch (e) {
            console.warn('Failed to store initial data:', e);
          }

          let cachedData = null;
          try {
            const cached = getFunctionalItem(CACHE_KEY);
            if (cached) {
              cachedData = JSON.parse(cached);
              if (cachedData.birthDate && typeof cachedData.birthDate === 'string') {
                cachedData.birthDate = new Date(cachedData.birthDate);
              }
              cachedData.acceptsCustomerBookings = cachedData.acceptsCustomerBookings !== false;
            }
          } catch (e) {
            console.warn('Failed to load cached data:', e);
          }

          setFormData(cachedData || initialFormData);
        } catch (error) {
          console.error('Error loading team member:', error);
          setSnackbar({
            open: true,
            message: t('teamMembers.update.error.loading'),
            severity: 'error'
          });
        } finally {
          setLoading(false);
        }
      });
    };

    if (storeId && teamMemberId) {
      loadTeamMember();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamMemberId, storeId]);

  // Cache form data on changes (but not during initial load)
  useEffect(() => {
    if (loading || !initialFormDataRef.current) return;
    
    try {
      setFunctionalItem(CACHE_KEY, JSON.stringify(formData));
    } catch (e) {
      console.warn('Failed to cache form data:', e);
    }
  }, [formData, loading, CACHE_KEY]);

  useEffect(() => {
    return () => {
      if (isCompletingUpdate.current) {
        return;
      }

      try {
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(INITIAL_DATA_KEY);
      } catch (e) {
        console.warn('Failed to clear team member update cache:', e);
      }
    };
  }, [CACHE_KEY, INITIAL_DATA_KEY]);

  // Rollback handler - restore initial data
  const handleRollback = () => {
    try {
      // Clear cache
      localStorage.removeItem(CACHE_KEY);
      
      // Restore initial data
      const initialJson = getFunctionalItem(INITIAL_DATA_KEY);
      if (initialJson) {
        const initialData = JSON.parse(initialJson);
        // Convert birthDate string back to Date object if it exists
        if (initialData.birthDate && typeof initialData.birthDate === 'string') {
          initialData.birthDate = new Date(initialData.birthDate);
        }
        setFormData(initialData);
        initialFormDataRef.current = initialData;
      } else if (initialFormDataRef.current) {
        setFormData(initialFormDataRef.current);
      }
      
      // Reset to first step
      setActiveStep(0);
      
      setSnackbar({
        open: true,
        message: t('teamMembers.update.discardSuccess') || 'Changes discarded. Form reset to original data.',
        severity: 'info'
      });
    } catch (e) {
      console.error('Failed to discard changes:', e);
      setSnackbar({
        open: true,
        message: t('teamMembers.update.discardError') || 'Failed to discard changes.',
        severity: 'error'
      });
    }
  };


  // Load services for the current store
  useEffect(() => {
    const loadServices = async () => {
      if (!storeId || servicesLoadingRef.current || isCompletingUpdate.current) return;

      try {
        servicesLoadingRef.current = true;
        const servicesData = await TeamMemberService.getServicesByStore(storeId);
        setServices(servicesData);
      } catch (error) {
        console.error('Failed to load services:', error);
        setSnackbar({
          open: true,
          message: t('teamMembers.registration.error.loadingServices'),
          severity: 'error'
        });
      } finally {
        servicesLoadingRef.current = false;
      }
    };

    loadServices();
  }, [storeId, t]);

  const steps = [
    {
      label: t('teamMembers.update.steps.step1.title'),
      description: t('teamMembers.update.steps.step1.description'),
      icon: <PersonIcon />
    },
    {
      label: t('teamMembers.update.steps.step2.title'),
      description: t('teamMembers.update.steps.step2.description'),
      icon: <LockIcon />
    },
    {
      label: t('teamMembers.update.steps.step3.title'),
      description: t('teamMembers.update.steps.step3.description'),
      icon: <ServicesIcon />
    },
    {
      label: t('teamMembers.update.steps.step4.title'),
      description: t('teamMembers.update.steps.step4.description'),
      icon: <ReviewIcon />
    }
  ];

  const lastValidatedStepIndex = steps.length - 2;

  const getStepErrors = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Personal Info (including Phone)
        if (!formData.name.trim()) {
          newErrors.name = t('teamMembers.registration.validation.nameRequired');
        }
        if (!formData.phoneNumber.trim()) {
          newErrors.phoneNumber = t('teamMembers.registration.validation.phoneRequired');
        }
        break;

      case 1: // Role & Permissions (no validation needed - all optional)
        break;

      case 2: // Services
        // No validation needed - team members can have no services assigned
        break;

      default:
        break;
    }

    return newErrors;
  };

  const validateStep = (step) => {
    const newErrors = getStepErrors(step);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStepsAndFocusFirstInvalid = (stepIndexes) => {
    for (const stepIndex of stepIndexes) {
      const stepErrors = getStepErrors(stepIndex);

      if (Object.keys(stepErrors).length > 0) {
        setErrors(stepErrors);
        setActiveStep(stepIndex);
        return false;
      }
    }

    return true;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep <= lastValidatedStepIndex && !validateStep(activeStep)) {
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (stepIndex) => {
    if (saving || stepIndex === activeStep) {
      return;
    }

    if (stepIndex > activeStep) {
      const lastStepBeforeTarget = Math.min(stepIndex - 1, lastValidatedStepIndex);
      const stepsToValidate = Array.from(
        { length: lastStepBeforeTarget + 1 },
        (_, index) => index
      );

      if (!validateStepsAndFocusFirstInvalid(stepsToValidate)) {
        return;
      }
    } else if (activeStep <= lastValidatedStepIndex && !validateStep(activeStep)) {
      return;
    }

    setActiveStep(stepIndex);
  };

  const handleSubmit = async () => {
    const stepsToValidate = Array.from({ length: lastValidatedStepIndex + 1 }, (_, index) => index);

    if (!validateStepsAndFocusFirstInvalid(stepsToValidate)) {
      return;
    }

    setSaving(true);
    isCompletingUpdate.current = true;

    try {
      // Convert Date object to yyyy-mm-dd for backend
      let birthDateForBackend = null;
      if (formData.birthDate) {
        const year = formData.birthDate.getFullYear();
        const month = String(formData.birthDate.getMonth() + 1).padStart(2, '0');
        const day = String(formData.birthDate.getDate()).padStart(2, '0');
        birthDateForBackend = `${year}-${month}-${day}`;
      }

      const transformedServices = (formData.services || []).map(service => ({
        serviceId: service.serviceId,
        customPrice: service.customPrice || null
      }));

      const teamMemberData = {
        name: formData.name,
        phone: {
          countryCode: formData.phoneCountryCode,
          number: formData.phoneNumber
        },
        genre: formData.genre || null,
        ...(birthDateForBackend && { birthDate: birthDateForBackend }),
        services: transformedServices,
        language: formData.language.toUpperCase(),
        storeId: storeId,
        roleId: formData.roleId || null,
        jobTitle: formData.jobTitle?.trim() || null,
        additionalPermissions: formData.additionalPermissions || [],
        acceptsCustomerBookings: formData.acceptsCustomerBookings !== false
      };

      await TeamMemberService.updateTeamMember(teamMemberId, teamMemberData);
      
      // Clear cache on successful save
      try {
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(INITIAL_DATA_KEY);
      } catch (e) {
        console.warn('Failed to clear cache:', e);
      }

      setSnackbar({
        open: true,
        message: t('teamMembers.update.success.updated'),
        severity: 'success'
      });

      // Navigate back to team members page after a short delay
      setTimeout(() => {
        navigate(`/management/stores/${storeId}/team-members`);
      }, 2000);

    } catch (error) {
      console.error('Failed to update team member:', error);
      setSnackbar({
        open: true,
        message: t('teamMembers.update.error.update'),
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));

    // Clear errors for fields that are being updated
    setErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(updates).forEach(key => {
        if (updates[key] && updates[key].toString().trim()) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <TeamMemberPersonalInfo
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            mode="update"
            savedAcceptsCustomerBookings={savedAcceptsCustomerBookings}
          />
        );
      case 1:
        return <TeamMemberRolePermissions formData={formData} updateFormData={updateFormData} errors={errors} storeId={storeId} i18n={i18n} />;
      case 2:
        return <TeamMemberServices formData={formData} updateFormData={updateFormData} services={services} errors={errors} currency={currentStore?.currency || DEFAULT_CURRENCY} />;
      case 3:
        return (
          <TeamMemberReview
            formData={formData}
            currentStore={currentStore}
            services={services}
            onRollback={handleRollback}
            showRollback={true}
            loading={loading}
            saving={saving}
            isUpdate={true}
            savedAcceptsCustomerBookings={savedAcceptsCustomerBookings}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <ManagementPageSkeleton
        pageTitle={t('teamMembers.update.title')}
        pageSubtitle={t('teamMembers.update.subtitle')}
        variant="form"
        contentSx={{
          maxWidth: { lg: '960px' },
          width: { xs: '100%', lg: '960px' }
        }}
      />
    );
  }

  return (
    <ManagementPageLayout
      id="team-member-update-page"
      containerId="team-member-update-header"
      titleId="team-member-update-title"
      subtitleId="team-member-update-subtitle"
      pageTitle={formData.name || t('teamMembers.update.title')}
      pageSubtitle={t('teamMembers.update.subtitle')}
      contentId="team-member-update-main-content"
      contentSx={{
        maxWidth: { lg: '960px' },
        width: { xs: '100%', lg: '960px' }
      }}
    >
      <ManagementPageCard id="team-member-update-card">
          {/* Custom Stepper */}
          <Box id="team-member-update-stepper-container" sx={{
            backgroundColor: BRAND.surface,
            borderBottom: `1px solid ${BRAND.line}`,
            p: 4
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'stretch',
              position: 'relative'
            }}>
              {steps.map((step, index) => (
                <Box key={index} sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                  position: 'relative',
                  overflow: 'visible',
                  justifyContent: 'flex-start'
                }}>
                  {/* Step Circle */}
                  <Box
                    component="button"
                    type="button"
                    id={`team-member-update-step-icon-${index + 1}`}
                    onClick={() => handleStepClick(index)}
                    aria-label={`Go to ${step.label}`}
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      background: activeStep >= index ? BRAND.accent : BRAND.surface,
                      color: activeStep >= index ? BRAND.onAccent : BRAND.textSubtle,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.4rem',
                      fontWeight: 'bold',
                      border: `3px solid ${activeStep >= index ? BRAND.accent : BRAND.line}`,
                      boxShadow: 'none',
                      transition: 'all 0.3s ease',
                      zIndex: 2,
                      position: 'relative',
                      cursor: saving ? 'default' : 'pointer',
                      padding: 0,
                      outline: 'none',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      '&:hover': saving ? undefined : {
                        transform: 'translateY(-1px)',
                        borderColor: BRAND.accent,
                      },
                      '&:focus-visible': {
                        boxShadow: `0 0 0 3px ${brandAlpha('accent', 0.35)}`,
                      },
                      '&:disabled': {
                        cursor: 'default',
                      }
                    }}
                  >
                    {activeStep > index ? <CheckIcon /> : step.icon}
                  </Box>

                  {/* Step Label */}
                  <Typography
                    id={`team-member-update-step-label-${index + 1}`}
                    sx={{
                      color: activeStep >= index ? BRAND.text : BRAND.textMuted,
                      fontWeight: activeStep >= index ? 700 : 500,
                      fontSize: '0.9rem',
                      mt: 1.5,
                      textAlign: 'center',
                      display: { xs: 'none', sm: 'block' },
                      lineHeight: '1.25rem',
                      width: '100%',
                      flex: 1
                    }}
                  >
                    {step.label}
                  </Typography>

                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <Box sx={{
                      position: 'absolute',
                      top: '25px',
                      left: 'calc(50% + 25px)',
                      right: 'calc(-50% + 25px)',
                      height: '3px',
                      background: activeStep > index ? BRAND.accent : BRAND.line,
                      zIndex: 1
                    }} />
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Step Content */}
          <Box id="team-member-update-step-content" sx={{ p: { xs: 1, sm: 4, md: 6 } }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                id={`team-member-update-step-${activeStep + 1}-content`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderStepContent(activeStep)}
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <WizardStepNavigation
              id="team-member-update-navigation"
              backId="team-member-update-back-button"
              nextId={activeStep === steps.length - 1 ? 'team-member-update-submit-button' : 'team-member-update-next-button'}
              backLabel={t('common.back')}
              nextLabel={activeStep === steps.length - 1 ? t('teamMembers.update.update') : t('common.next')}
              onBack={handleBack}
              onNext={activeStep === steps.length - 1 ? handleSubmit : handleNext}
              backDisabled={activeStep === 0}
              nextDisabled={saving}
              isLastStep={activeStep === steps.length - 1}
              nextLoading={saving}
            />
          </Box>
        </ManagementPageCard>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ManagementPageLayout>
  );
};

export default TeamMemberUpdatePage;

